import React, { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
} from './ui/drawer';
import { MarkHoverCardShell } from './MarkHoverCardShell';
import { CommentCard } from './CommentCard';
import { scrollToComment } from '../utils/editor-utils';
import { getCommentLocationLostToastMessage } from '../utils/comment-location-feedback';
import { API_BASE } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import type { Editor } from '@tiptap/core';
import { useDeleteComment } from '../hooks/use-delete-comment';
import type { MentionableEntity } from './RichTextField';

type Comment = {
  id: string;
  documentId: string;
  userId: string;
  userEmail?: string;
  content: string;
  fromPos: number;
  toPos: number;
  selectedText: string;
  createdAt: string;
  updatedAt: string;
  replies?: Array<{
    id: string;
    userId: string;
    userEmail?: string;
    createdAt: string;
    comment: string;
  }>;
};

type CommentHoverCardProps = {
  editor: Editor | null;
  hoveredComment: Comment | null;
  hoveredCommentPosition: { top: number; left: number } | null;
  commentHoverCardContentRef: React.RefObject<HTMLDivElement>;
  isMobile: boolean;
  documentOwnerId: string | null;
  onLoadComments: () => Promise<void>;
  onClose: () => void;
  onHoverCardMouseEnter: () => void;
  onHoverCardMouseLeave: () => void;
  setKeepCommentHoverOpen?: (keepOpen: boolean) => void;
  scrollContainerRef?: React.RefObject<HTMLElement>;
  setHoveredComment?: (comment: Comment | null) => void;
  mentionableEntities?: MentionableEntity[];
};

export const CommentHoverCard: React.FC<CommentHoverCardProps> = ({
  editor,
  hoveredComment,
  hoveredCommentPosition,
  commentHoverCardContentRef,
  isMobile,
  documentOwnerId,
  onLoadComments,
  onClose,
  onHoverCardMouseEnter,
  onHoverCardMouseLeave,
  setKeepCommentHoverOpen,
  scrollContainerRef,
  setHoveredComment,
  mentionableEntities = [],
}) => {
  const { token } = useAuth();
  const { t } = useLocale();
  const { showToast } = useToast();
  const [hasReplyDraft, setHasReplyDraft] = useState(false);
  const { deleteComment } = useDeleteComment({
    onCommentsUpdated: onLoadComments,
  });

  useEffect(() => {
    if (!hoveredComment) {
      setHasReplyDraft(false);
    }
  }, [hoveredComment]);

  const handleReplyDraftChange = (hasDraft: boolean): void => {
    setHasReplyDraft(hasDraft);
    setKeepCommentHoverOpen?.(hasDraft);
  };

  const handleCloseRequest = (): void => {
    if (hasReplyDraft) {
      return;
    }
    onClose();
  };

  const handleDismiss = (): void => {
    setHasReplyDraft(false);
    setKeepCommentHoverOpen?.(false);
    onClose();
  };

  if (!hoveredComment) {
    return null;
  }

  const handleScrollToComment = (comment: Comment): void => {
    const result = scrollToComment(editor, comment, scrollContainerRef);
    const message = getCommentLocationLostToastMessage(result, t);
    if (message) {
      showToast(message);
    }
    onClose();
  };

  const handleDeleteComment = async (commentId: string): Promise<void> => {
    const deleted = await deleteComment(commentId);
    if (deleted) {
      onClose();
    }
  };

  const handleAddReply = async (commentId: string, content: string): Promise<void> => {
    if (!token) {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/comments/${commentId}/replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content }),
      });
      if (!response.ok) {
        console.error('Failed to add reply');
        return;
      }
      const updatedComment = await response.json();
      if (setHoveredComment && updatedComment) {
        setHoveredComment(updatedComment);
      }
      await onLoadComments();
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  const handleDeleteReply = async (commentId: string, replyId: string): Promise<void> => {
    if (!token) {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/comments/${commentId}/replies/${replyId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        console.error('Failed to delete reply');
        return;
      }
      const data = await response.json();
      if (setHoveredComment && data.comment) {
        setHoveredComment(data.comment);
      }
      await onLoadComments();
    } catch (error) {
      console.error('Error deleting reply:', error);
    }
  };

  const commentCard = (
    <CommentCard
      key={hoveredComment.id}
      comment={hoveredComment}
      documentOwnerId={documentOwnerId}
      isMobileDrawer={isMobile}
      onScrollToComment={handleScrollToComment}
      onDeleteComment={handleDeleteComment}
      onAddReply={handleAddReply}
      onDeleteReply={handleDeleteReply}
      onReplyDraftChange={handleReplyDraftChange}
      onDismiss={handleDismiss}
      showDeleteReplyButton={false}
      mentionableEntities={mentionableEntities}
    />
  );

  if (isMobile) {
    return (
      <Drawer open={!!hoveredComment} onOpenChange={(open) => {
        if (!open) {
          handleCloseRequest();
        }
      }}>
        <DrawerContent className="bg-card text-card-foreground border-t border-border">
          <DrawerHeader className="p-0">
            {commentCard}
          </DrawerHeader>
        </DrawerContent>
      </Drawer>
    );
  }

  if (!hoveredCommentPosition) {
    return null;
  }

  return (
    <MarkHoverCardShell
      hoveredPosition={hoveredCommentPosition}
      hoverCardContentRef={commentHoverCardContentRef}
      onClose={handleCloseRequest}
      onMouseEnter={onHoverCardMouseEnter}
      onMouseLeave={onHoverCardMouseLeave}
      contentClassName="max-w-2xl w-auto p-0 border-0 bg-transparent shadow-none"
    >
      {commentCard}
    </MarkHoverCardShell>
  );
};
